/**
 * O que o cliente faz com a carta depois de contemplado — as duas estratégias
 * de patrimônio que o Lucas apresenta ao cliente de ticket alto:
 *
 * ── CRÉDITO APLICADO ─────────────────────────────────────────────────────
 * O crédito líquido vai para uma aplicação a 100% do CDI e as parcelas
 * restantes saem dela, mês a mês. Se o saldo zera, o cliente cobre do bolso
 * (o saldo fica negativo e não rende). O IR é 15% sobre o rendimento total no
 * fim — a alíquota de aplicação longa; os resgates mensais de parcela, a rigor,
 * teriam alíquotas da tabela regressiva, e a diferença é pequena no prazo de
 * um consórcio.
 *
 * ── IMÓVEL E ALUGUEL ─────────────────────────────────────────────────────
 * O crédito líquido compra um imóvel que é alugado a partir do mês seguinte.
 * O imóvel valoriza a cada 12 meses completos (IVG-R do BCB como padrão) e o aluguel
 * acompanha o valor do imóvel. O cliente paga as parcelas restantes; o
 * resultado é o patrimônio (imóvel + aluguéis) menos tudo o que pagou.
 *
 * As duas contas recebem a série de parcelas pós-contemplação já pronta
 * (parcelasDoPlano), então batem com o resumo e com a tabela ano a ano.
 */
import { IR_GANHO_CAPITAL, mensalDeAnual } from './comparativo.ts';

export type EntradaPatrimonio = {
  /** Crédito líquido liberado na contemplação. */
  credito: number;
  /** Parcelas + lance pago até contemplar. */
  dinheiroNovoAteContemplar: number;
  /** Parcelas depois da contemplação, mês a mês. */
  parcelasDepois: number[];
};

export type CreditoAplicado = {
  meses: number;
  rendimentoBruto: number;
  ir: number;
  parcelasPagas: number;
  /** Saldo da aplicação no fim do plano, depois do IR. */
  saldoFinal: number;
  /** Saldo final − dinheiro novo até contemplar. */
  resultado: number;
  /** Saldo da aplicação ao fim de cada mês, já descontado o IR do rendimento até ali (a ponta = saldoFinal). */
  serie: number[];
};

export const simularCreditoAplicado = (e: EntradaPatrimonio, cdiAa: number): CreditoAplicado => {
  const i = mensalDeAnual(cdiAa);
  let saldo = e.credito;
  let rendimentoBruto = 0;
  let parcelasPagas = 0;
  const serie: number[] = [];
  for (const p of e.parcelasDepois) {
    const rendimento = saldo > 0 ? saldo * i : 0;
    rendimentoBruto += rendimento;
    parcelasPagas += p;
    saldo += rendimento - p;
    serie.push(saldo - rendimentoBruto * IR_GANHO_CAPITAL);
  }
  const ir = rendimentoBruto * IR_GANHO_CAPITAL;
  const saldoFinal = saldo - ir;
  return {
    meses: e.parcelasDepois.length,
    rendimentoBruto,
    ir,
    parcelasPagas,
    saldoFinal,
    resultado: saldoFinal - e.dinheiroNovoAteContemplar,
    serie,
  };
};

export type ImovelEAluguel = {
  meses: number;
  valorFinalImovel: number;
  alugueisAcumulados: number;
  parcelasPagas: number;
  primeiroAluguel: number;
  primeiraParcela: number;
  /** Quanto da primeira parcela o primeiro aluguel cobre, em %. */
  coberturaPct: number;
  /** Imóvel + aluguéis no fim do plano. */
  patrimonio: number;
  /** Patrimônio − tudo o que o cliente pagou (até e depois da contemplação). */
  resultado: number;
  /** Mês a mês: valor do imóvel e aluguéis acumulados. */
  serieImovel: number[];
  serieAlugueis: number[];
};

export const simularImovelEAluguel = (
  e: EntradaPatrimonio,
  premissas: { valorizacaoAa: number; aluguelAm: number },
): ImovelEAluguel => {
  const valorizacao = 1 + premissas.valorizacaoAa / 100;
  const taxaAluguel = premissas.aluguelAm / 100;
  let valor = e.credito;
  let alugueisAcumulados = 0;
  let parcelasPagas = 0;
  let primeiroAluguel = 0;
  const serieImovel: number[] = [];
  const serieAlugueis: number[] = [];
  e.parcelasDepois.forEach((p, idx) => {
    if (idx > 0 && idx % 12 === 0) valor *= valorizacao;
    const aluguel = valor * taxaAluguel;
    if (idx === 0) primeiroAluguel = aluguel;
    alugueisAcumulados += aluguel;
    parcelasPagas += p;
    serieImovel.push(valor);
    serieAlugueis.push(alugueisAcumulados);
  });
  const n = e.parcelasDepois.length;
  // Valorização só nos aniversários: o ano incompleto do fim não conta (conservador,
  // e o número do cartão é o mesmo da ponta do gráfico).
  const valorFinalImovel = valor;
  const primeiraParcela = e.parcelasDepois[0] ?? 0;
  const patrimonio = valorFinalImovel + alugueisAcumulados;
  return {
    meses: n,
    valorFinalImovel,
    alugueisAcumulados,
    parcelasPagas,
    primeiroAluguel,
    primeiraParcela,
    coberturaPct: primeiraParcela > 0 ? (primeiroAluguel / primeiraParcela) * 100 : 0,
    patrimonio,
    resultado: patrimonio - e.dinheiroNovoAteContemplar - parcelasPagas,
    serieImovel,
    serieAlugueis,
  };
};
